import { useQuery } from '@apollo/client'
import { startCase } from 'lodash'
import { useSubscriptionErrorReset } from 'hooks/useResetErrorSubscription'
import { Answer, Filter, Schema } from 'typings/filters'
import { Data } from 'components/Charts/types'
import { GET_FILTER_VALUES, SOLITARY_CONVERSION_RATE_QUERY } from 'query/query'
import { useAppliedFiltersAndContext } from 'hooks/useAppliedFiltersAndContext'
import { KBQSelectOption } from 'components/KBQSelect'
import { useKBQSelectedValueByPath } from 'hooks/useKBQSelectedValueByPath'

export interface FilterValuesResult {
  filterValues: Answer[]
}

export const LOST_SHOPPER_BRAND = 651
export const LOST_SHOPPERS = 'lost shoppers'
export const SWITCHERS = 'switchers'
export const SHOPPERS = 'shoppers'
export const PROMOTERS = 'promoters'

export const PageNameEnums = {
  BrandLostShoppers: 'brand-lost-shoppers',
  RetailerLostShoppers: 'retailer-lost-shoppers',
  BrandSwitchers: 'brand-switchers',
  NpsWithShopperVoices: 'nps-with-shopper-voices',
}
export const useKBQDropdownOptions = (
  variableId: number,
  localFilters?: Pick<Filter, 'variable_id' | 'answer_code'>[]
) => {
  const { appliedFilters, context } = useAppliedFiltersAndContext()
  const { defaultBrand } = useKBQSelectedValueByPath()
  const {
    data: lostShoppersBrandsAsFilters,
    loading: filtersLoading,
    error: filtersError,
  } = useQuery<FilterValuesResult>(GET_FILTER_VALUES, {
    variables: { context, variableId, filters: [], schema: Schema.CIE },
  })

  const {
    data: brands,
    loading: brandsLoading,
    error: brandsError,
  } = useQuery<Data>(SOLITARY_CONVERSION_RATE_QUERY, {
    variables: {
      primaryVar: 'consideredBrands',
      secondaryVar: 'brandLostShopper',
      limitDataPoints: 10,
      totalVariableSelectionOverride: 'consideredBrands',
      pageNumber: 1,
      filters: appliedFilters,
      context,
      localFilters,
      schema: Schema.CIE,
    },
  })

  const error = filtersError ?? brandsError

  const loading = filtersLoading || brandsLoading
  let data
  if (brands && !loading) {
    data = Object.assign({}, brands, {
      cross: {
        ...brands.cross,
        series: brands.cross.series,
      },
    })
  }

  const sortedDataPoints = data?.cross.series[0].dataPoints
    .slice()
    .sort((a, b) => a.secondaryValue.localeCompare(b.secondaryValue))
  const topLostShoppers = !loading
    ? lostShoppersBrandsAsFilters?.filterValues.reduce<KBQSelectOption[]>(
        (acc, { label, answer_id, answer_code }) => {
          sortedDataPoints?.forEach((brandValues) => {
            if (Object.values(brandValues).includes(label)) {
              acc.push({
                label: startCase(label),
                answer_id,
                answer_code,
                variable_id: variableId,
              })
            }
          })
          return acc
        },
        []
      )
    : undefined

  const topDataFromBrand = lostShoppersBrandsAsFilters?.filterValues.filter(
    (value) => value.label === defaultBrand?.label
  )[0]

  useSubscriptionErrorReset(error)
  return {
    topLostShoppers,
    loading,
    error: !brands ? error : undefined,
    topBrandData: topDataFromBrand,
  }
}
